import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const role = user?.role || 'admin';
  const isAdmin = role === 'admin';
  const isViewer = role === 'viewer';
  const canManage = role === 'admin' || role === 'capture_manager';

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch {
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const lookupUei = async (uei, signal) => {
    try {
      const response = await axios.post(`${API}/auth/lookup-uei`, { uei }, { signal });
      return response.data;
    } catch (error) {
      if (error.response?.status === 503 && error.response?.data?.lookup_status === 'upstream_error') {
        return error.response.data;
      }
      throw error;
    }
  };

  const getUeiLookupStatus = async (lookupId, signal) => {
    const response = await axios.get(`${API}/auth/lookup-uei-status/${lookupId}`, { signal });
    return response.data;
  };

  const register = async (registration) => {
    // New OTP flow: /auth/register sends a code, does NOT create the user.
    const response = await axios.post(`${API}/auth/register`, registration);
    return response.data; // { pending_verification: true, email }
  };

  const verifyOtp = async (email, code) => {
    const response = await axios.post(`${API}/auth/verify-otp`, { email, code });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const resendOtp = async (email) => {
    const response = await axios.post(`${API}/auth/resend-otp`, { email });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, lookupUei, getUeiLookupStatus, register, verifyOtp, resendOtp, logout, updateUser, role, isAdmin, isViewer, canManage }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
